import { IgAccTimeEnum } from '@core/models/ApiFields/ig-entities.enums';
import { IgAccount, IgAccTime, IgMedia } from '@core/models/ApiFields/ig-entities';
import { Paginated, OnlyPagination, Params, RawDataWithBreakd, ReqMode } from '@core/models/IApi';
import { Dimension } from '@features/social-analysis/models/IDimensions';
import { Sorting } from '@features/social-analysis/models/ITable';
import {
    IG_ENDPOINT,
    insightsTableFields,
} from '@features/social-analysis/social-listening/config/api-params';
import { InsightsTableState } from '@features/social-analysis/social-shared/store/ins-table.store';
import { PageFiltersState } from '@features/social-analysis/social-shared/store/page-filters.store';
import { finalize, map } from 'rxjs/operators';
import { HttpBuilder } from '../../social-shared/services/http/http-builder';

export class InsightsTableHttpBuilder extends HttpBuilder<InsightsTableState, RawDataWithBreakd> {
    reqData(state: InsightsTableState, pageFilters: PageFiltersState, needsTotal?: boolean) {
        const isOnlyAccDimSelected = this.isOnlyAccDimSelected(state.dimensions());

        const url = this.buildUrl(IG_ENDPOINT);
        const params = this.buildParams(state, pageFilters, 1, isOnlyAccDimSelected);

        state.isLoading.set(true);
        this.reqDataWithBreakdowns(url, params, state);

        if (needsTotal) this.reqTotal(state, pageFilters);
    }

    private reqDataWithBreakdowns(url: string, params: Params, state: InsightsTableState) {
        {
            this.http
                .get<Paginated<RawDataWithBreakd[]>>(url, { params })
                .pipe(
                    map(res => this.formatResWithBreakdowns(res, state.dimensions())),
                    finalize(() => state.isLoading.set(false))
                )
                .subscribe({
                    next: data => this.handleSuccess(state, data),
                    error: err => this.handleError(err?.error?.message),
                });
        }
    }

    reqTotal(state: InsightsTableState, pageFilters: PageFiltersState) {
        const isOnlyAccDimSelected = this.isOnlyAccDimSelected(state.dimensions());

        const url = this.buildUrl(IG_ENDPOINT);
        const params = this.buildParams(state, pageFilters, 2, isOnlyAccDimSelected);

        state.isLoading.set(true);

        this.http.get<OnlyPagination>(url, { params: params }).subscribe({
            next: res => state.total.set(res.total),
            error: err => this.handleError(err?.error?.message),
        });
    }

    protected isTimeDimSelected(dims: Dimension[]) {
        const timeDims: string[] = [
            IgAccTimeEnum.fields.date,
            IgAccTimeEnum.fields.week,
            IgAccTimeEnum.fields.month,
        ];
        const values = dims.map(dim => dim.value);
        return values.some(dim => timeDims.includes(dim));
    }

    protected isOnlyAccDimSelected(dims: Dimension[]) {
        const entities = [IgAccount.prefix, IgAccTime.prefix];
        // return dims.every(dim => dim?.prefix && entities.includes(dim.prefix));
        return dims.every(dim => {
            const split = dim.value.split('.');
            if (split.length < 2) return true;
            return entities.includes(split[0]);
        });
    }

    protected buildParams(
        { dimensions, limit, offset, sorting }: InsightsTableState,
        pageFilters: PageFiltersState,
        mode: ReqMode,
        isOnlyAccDimSelected?: boolean
    ) {
        const dates = this.getDates(pageFilters.selectedDates());
        const [fields, breakdown] = this.getFields(dimensions(), isOnlyAccDimSelected);
        return {
            ...dates,
            fields: fields,
            breakdowns: breakdown,
            limit: limit(),
            offset: offset(),
            sort: this.formatSorting(sorting()),
            mode: mode,
            ...this.getMediaTypeFilter(),
            ...this.formatFilters(pageFilters.appliedFilters()),
        };
    }

    protected getFields(dims: Dimension[], isOnlyAccDimSelected?: boolean): [string, string] {
        const dimsClone = [...dims];
        const fields: string[] = [];
        const breakdowns: string[] = [];

        if (dims.length > 1) fields.push(dimsClone.pop()!.value);
        breakdowns.push(...dimsClone.map(dim => dim.value));

        if (breakdowns.includes(IgAccount.getPrefixed('igAccount')))
            breakdowns.push(IgAccount.getPrefixed('image'));

        fields.push(...insightsTableFields.igMedia);
        if (isOnlyAccDimSelected) fields.push(...insightsTableFields.igAccTime);

        return [fields.join(), breakdowns.join()];
    }

    protected formatSorting(sorting: Sorting[]): string {
        return sorting.map(sort => `${sort.prop}_${sort.dir}`).join();
    }

    protected getMediaTypeFilter() {
        return { [IgMedia.getPrefixed('mediaType')]: 'neq:tag' };
    }

    protected formatResWithBreakdowns(res: Paginated<RawDataWithBreakd[]>, dims: Dimension[]) {
        const [fields, breakdowns] = this.getFields(dims);
        const reqBreakdowns = breakdowns.split(',');

        const formatted = res.data.map(data => {
            const breakdowns: Record<string, string> = this.flattenOneLevel(data.breakdown);
            const breakdRows = reqBreakdowns.reduce((rows: any, key) => {
                const imgField = IgAccount.getPrefixed('image');
                const accField = IgAccount.getPrefixed('igAccount');

                if (key === imgField) return rows;
                const subHeaderRow = { [key]: breakdowns[key], isSubHeader: true };
                if (key === accField) subHeaderRow[imgField] = breakdowns[imgField];

                return [...rows, subHeaderRow];
            }, []);

            const groupedData = data.data.map(item => this.flattenOneLevel(item));

            return [...breakdRows, ...groupedData];
        });

        return formatted.flat();
    }
}

export class SocListnInsTableHttpBuilder extends InsightsTableHttpBuilder {}
